<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import MSEPlayer from '@flussonic/flussonic-mse-player'
import StreamStatsService from '@/services/StreamStatsService'
import IconBase from '@/components/icon/IconBase.vue'
import IconCamera from '@/components/icon/IconCamera.vue'

/**
 * One live source inside the preview canvas: the same Flussonic MSE-LD
 * low-latency path StreamPlayer uses (`wss://<hostId>.castr.io/<name>/mse_ld`),
 * resolved from the stream's own pulse. Owns its player instance, so many
 * layers can coexist (StreamPlayer is single-instance by design).
 */
const props = defineProps({
	stream: { type: Object, default: null },
	label: { type: String, default: '' },
	muted: { type: Boolean, default: true },
	volume: { type: Number, default: 1 },
	/** Still placeholder instead of decoding video. */
	lightweight: { type: Boolean, default: false },
	/** Decode only while the layer is actually drawn. */
	active: { type: Boolean, default: true },
})

/** `aspect`: the playing picture's width / height, or null while the placeholder fills the layer. */
const emit = defineEmits(['aspect'])

const videoEl = ref(null)
const pulse = ref(null)
const playing = ref(false)
let player = null
let pulseTimer = null
let alive = true
let startedAt = 0
let lastProgressAt = 0
/** No first frame / no new data for this long → reconnect on the next pulse. */
const STALL_MS = 15000

const streamKey = computed(() => props.stream?.key || '')
const online = computed(() => !!pulse.value?.alive)

const playbackUrl = computed(() => {
	const p = pulse.value
	if (!p?.alive || !p.hostId) return ''
	const hostId = String(p.hostId)
	const path = /\w+-\d+/i.test(hostId) && p.name ? (p.staticPrefix ? `static/${p.name}` : p.name) : streamKey.value
	return `wss://${hostId}.castr.io/${path}/mse_ld?tracks=v1a1`
})

const shouldPlay = computed(() => props.active && !props.lightweight && !!playbackUrl.value)

const stopPlayer = () => {
	if (!player) return
	try { player.pause() } catch (e) { /* noop */ }
	try { player.stop() } catch (e) { /* noop */ }
	player = null
	playing.value = false
}

const startPlayer = () => {
	if (!alive || player || !videoEl.value || !playbackUrl.value) return
	try {
		player = new MSEPlayer(videoEl.value, playbackUrl.value, {
			bufferMode: 'sequence',
			statsSendEnable: false,
			// Edges can deliver MSE-LD in multi-second bursts. The default
			// maxBufferDelay (2s) seeks to the buffer end on every burst and then
			// freezes until the next one, so allow a deep buffer.
			maxBufferDelay: 20,
			onProgress: () => { playing.value = true; lastProgressAt = Date.now() },
			onError: () => {
				// The next pulse reconnects (see reviveIfStuck).
				stopPlayer()
			},
		})
		startedAt = Date.now()
		applyAudio()
		player.play()
	} catch (err) {
		console.error('live layer player failed', err)
		stopPlayer()
	}
}

const applyAudio = () => {
	if (!videoEl.value) return
	videoEl.value.muted = props.muted
	videoEl.value.volume = Math.max(0, Math.min(1, props.volume))
}

const pollPulse = async () => {
	if (!alive || !streamKey.value) return
	try {
		const next = await StreamStatsService.getStreamMediaPulse(streamKey.value)
		if (!alive) return
		pulse.value = next || null
	} catch (e) {
		if (alive) pulse.value = null
	}
	reviveIfStuck()
	if (alive) pulseTimer = setTimeout(pollPulse, document.hidden ? 20000 : 10000)
}

// `shouldPlay` stays true across an error or a silent edge, so its watcher
// never fires again; without this a layer sits on "Connecting…" for good.
const reviveIfStuck = () => {
	if (!alive || !shouldPlay.value) return
	if (!player) { startPlayer(); return }
	const since = playing.value ? lastProgressAt : startedAt
	if (Date.now() - since > STALL_MS) { stopPlayer(); startPlayer() }
}

watch(shouldPlay, (play) => {
	if (play) startPlayer()
	else stopPlayer()
})
watch(playbackUrl, (url, prev) => {
	if (url !== prev && player) { stopPlayer(); if (shouldPlay.value) startPlayer() }
})
watch(() => [props.muted, props.volume], applyAudio)
const showing = computed(() => shouldPlay.value && playing.value)
const reportAspect = () => {
	const v = videoEl.value
	emit('aspect', showing.value && v?.videoWidth && v?.videoHeight ? v.videoWidth / v.videoHeight : null)
}
watch(showing, reportAspect)
watch(streamKey, () => { stopPlayer(); pulse.value = null; clearTimeout(pulseTimer); pollPulse() })

onMounted(() => { pollPulse() })
onBeforeUnmount(() => {
	alive = false
	clearTimeout(pulseTimer)
	stopPlayer()
})
</script>

<template>
	<div class="relative h-full w-full overflow-hidden bg-black">
		<video
			v-show="shouldPlay && playing"
			ref="videoEl"
			class="h-full w-full object-contain"
			playsinline
			autoplay
			:muted="muted"
			@loadedmetadata="reportAspect"
			@resize="reportAspect"
		/>
		<div v-if="!(shouldPlay && playing)" class="live-layer-idle absolute inset-0 flex flex-col items-center justify-center gap-1 bg-surface-1 text-center" :data-online="online ? 'true' : 'false'" aria-hidden="true">
			<span class="flex size-[14%] min-h-[18px] min-w-[18px] max-h-9 max-w-9 items-center justify-center rounded-full ring-1" :class="online ? 'bg-success-transparent text-success ring-success' : 'bg-surface-3 text-surface-8 ring-surface-4'">
				<icon-base width="60%" height="60%"><icon-camera /></icon-base>
			</span>
			<span class="max-w-full truncate px-2 text-xs font-medium text-surface-text-1">{{ label || stream?.name || 'Livestream' }}</span>
			<span class="text-xs text-surface-8">
				<template v-if="!stream">Stream not found</template>
				<template v-else-if="lightweight">Lightweight · {{ online ? 'live' : 'offline' }}</template>
				<template v-else-if="!online">Offline</template>
				<template v-else>Connecting…</template>
			</span>
		</div>
	</div>
</template>

<style scoped>
.live-layer-idle {
	background-image: repeating-linear-gradient(135deg, transparent 0 10px, rgba(255, 255, 255, 0.03) 10px 20px);
}
</style>
